import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, Button, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import CheckoutLegalProceedNotice from '@/components/legal/checkout-legal-proceed-notice';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import Modal from '@/components/common/modal';
import BillingAddressDisplay from '@/components/events/event-catalog/billing-address-display';
import BillingForm from '@/components/organizations/edit-active-organization/tabs/billing/billing.form';
import OrganizationSubscriptionPricingDisplay from '@/components/organizations/plan/organization-subscription-pricing-display';
import EventManagePlanDialog from '@/components/events/plan/event-manage-plan-dialog';
import { useBreakpointUp } from '@/hooks/use-breakpoint';
import { useNotifications } from '@/hooks/use-notifications';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import { useFeatureAccess } from '@/hooks/use-feature-access';
import { useActiveOrganizationStore } from '@/state/stores/use-organization-store';
import { usePermissionsQuery } from '@/state/queries/permissions/use-permissions';
import { useGetOrganizationQuery } from '@/state/queries/organizations/use-get-organization';
import { useEditOrganizationMutation } from '@/state/mutations/organizations/use-edit-organization';
import { useTaxClassificationMutation } from '@/state/mutations/vat-validation/use-tax-classification';
import { useSubscriptionPlansQuery } from '@/state/queries/transactions/use-subscription-plans';
import { useEventSubscriptionCheckoutCartQuery } from '@/state/queries/transactions/use-event-subscription-checkout-cart';
import { usePatchEventSubscriptionCheckoutCart } from '@/state/mutations/transactions/use-patch-event-subscription-checkout-cart';
import { useCheckoutEventSubscriptionCart } from '@/state/mutations/transactions/use-checkout-event-subscription-cart';
import { editActiveOrganizationSchemaFactory } from '@/schemas/organizations/edit-active-organization';
import type { EditActiveOrganization } from '@/components/organizations/edit-active-organization/edit.modal';
import type { Address } from '@/types/dtos/organizations/address.dto';
import type { PatchOrganizationDto } from '@/types/dtos/organizations/request/patch-organization.dto';
import type { OrganizationDto } from '@/types/dtos/organizations/organization.dto';
import type { TaxClassification } from '@/types/dtos/organizations/response/tax-classification.dto';
import type { SubscriptionPlan } from '@/types/dtos/transactions/subscription-plan.dto';
import type { SubscriptionCheckoutCartTotals } from '@/state/queries/transactions/use-subscription-checkout-cart';
import { ADDRESS_TYPE } from '@/enums/address.enum';
import { COUNTRY_TO_CODE_MAP } from '@/constants';
import { EVENT_PAID_TIERS, EVENT_SUBSCRIPTION_PRODUCT_KEYS } from '@/constants/event-subscription-product-keys';
import { capitalize } from '@/helpers/string.helper';
import {
  collectQuotaKeysFromPlan,
  formatQuotaStepperCountLine,
  includedQuantityForKey,
} from '@/helpers/organization-subscription-pricing';
import { resolveProductClaimText } from '@/helpers/resolve-translated-product-field';

type Props = {
  open: boolean;
  onClose: () => void;
  eventId: string;
};

type EditingLine = {
  subscriptionProductKey: string;
  scopeType: string;
  scopeId: string;
};

const PRODUCT_LINE_ORDER = [
  EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY,
  EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY_EVENT,
  EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY_FLOW,
  EVENT_SUBSCRIPTION_PRODUCT_KEYS.EVENT_BROADCAST,
  EVENT_SUBSCRIPTION_PRODUCT_KEYS.STAGE_READY,
] as const;

function productDisplayName(
  t: (key: TranslationKey, params?: Record<string, string | number>) => string,
  productKey: string,
): string {
  if (
    productKey === EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY ||
    productKey === EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY_EVENT
  ) {
    return t('event.plan.capacityLine');
  }
  if (productKey === EVENT_SUBSCRIPTION_PRODUCT_KEYS.BASE_CAPACITY_FLOW) {
    return t('event.plan.flowPlayoutLine');
  }
  if (productKey === EVENT_SUBSCRIPTION_PRODUCT_KEYS.EVENT_BROADCAST) return t('event.plan.broadcastLine');
  if (productKey === EVENT_SUBSCRIPTION_PRODUCT_KEYS.STAGE_READY) return t('event.plan.stageReadyLine');
  return productKey;
}

function tierLabel(t: (key: TranslationKey, params?: Record<string, string | number>) => string, tier: string): string {
  const k = `subscription.organization.tiers.${tier}` as TranslationKey;
  const r = t(k);
  return r === k ? tier : r;
}

function extraUnitsFor(units: { quotaKey: string; quantity: number }[] | undefined, quotaKey: string): number {
  return units?.find((u) => u.quotaKey === quotaKey)?.quantity ?? 0;
}

const createBillingAddress = (
  orgBillingAddress: Address | undefined,
  formData?: EditActiveOrganization['billingAddress'],
): Address =>
  ({
    ...(orgBillingAddress || {}),
    ...(formData || {}),
    type: ADDRESS_TYPE.BILLING,
  }) as Address;

const createFormDefaults = (
  organization: OrganizationDto | undefined,
  billingAddress: Address | undefined,
): EditActiveOrganization => ({
  general: { name: organization?.name || 'no name' },
  billingAddress: {
    name: billingAddress?.name || '',
    address1: billingAddress?.address1 || '',
    address2: billingAddress?.address2 || '',
    city: billingAddress?.city || '',
    country: billingAddress?.country || '',
    postCode: billingAddress?.postCode || '',
    vat: billingAddress?.vat || '',
  },
});

function CheckoutActionTooltip({
  wideScreen,
  title,
  children,
}: {
  wideScreen: boolean;
  title: string;
  children: ReactElement;
}) {
  if (!wideScreen) return children;
  return <Tooltip title={title}>{children}</Tooltip>;
}

export default function EventSubscriptionCheckoutModal({ open, onClose, eventId }: Props) {
  const t = useTranslations();
  const notification = useNotifications();
  const isWideScreen = useBreakpointUp('sm');
  const { activeOrganization, setActiveOrganization } = useActiveOrganizationStore();
  const [isEditingBillingAddress, setIsEditingBillingAddress] = useState(false);
  const [editingLine, setEditingLine] = useState<EditingLine | null>(null);
  const [taxClassification, setTaxClassification] = useState<TaxClassification>();

  const { data: checkoutCart, isPending: cartLoading } = useEventSubscriptionCheckoutCartQuery(eventId, open);
  const billingOrganizationId = checkoutCart?.billingOrganizationId ?? undefined;
  const { data: billingOrganization } = useGetOrganizationQuery(open ? billingOrganizationId : undefined);
  const { data: plansData } = useSubscriptionPlansQuery({ entityType: 'Event', enabled: open });
  const patchCart = usePatchEventSubscriptionCheckoutCart(eventId);
  const checkoutMutation = useCheckoutEventSubscriptionCart(eventId);
  const { mutateAsync: taxClassificationMutation } = useTaxClassificationMutation();
  const { mutateAsync: editOrganizationMutation, isPending: editOrgPending } = useEditOrganizationMutation();
  const { data: permissions } = usePermissionsQuery();
  const { hasAccess: hasMonetizeAccess, isLoading: monetizeAccessLoading } = useFeatureAccess('monetize');

  const cartLines = checkoutCart?.cart?.lines ?? [];
  const totals = checkoutCart?.totals;
  const patchPending = patchCart.isPending;

  const catalogByKeyTier = useMemo(() => {
    const m = new Map<string, SubscriptionPlan>();
    for (const p of plansData?.plans ?? []) {
      if (p.billingPeriod !== 'ONCE') continue;
      m.set(`${p.subscriptionProductKey}|${p.tier}`, p);
    }
    return m;
  }, [plansData?.plans]);

  const tierCountByProduct = useMemo(() => {
    const m = new Map<string, number>();
    for (const p of plansData?.plans ?? []) {
      if (p.billingPeriod !== 'ONCE') continue;
      if (!(EVENT_PAID_TIERS as readonly string[]).includes(p.tier)) continue;
      const key = p.subscriptionProductKey ?? '';
      if (!key) continue;
      m.set(key, (m.get(key) ?? 0) + 1);
    }
    return m;
  }, [plansData?.plans]);

  const orderedLineIndices = useMemo(() => {
    const indices = cartLines.map((_, i) => i);
    return indices.sort((a, b) => {
      const ka = cartLines[a]?.subscriptionProductKey ?? '';
      const kb = cartLines[b]?.subscriptionProductKey ?? '';
      const ia = PRODUCT_LINE_ORDER.indexOf(ka as (typeof PRODUCT_LINE_ORDER)[number]);
      const ib = PRODUCT_LINE_ORDER.indexOf(kb as (typeof PRODUCT_LINE_ORDER)[number]);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
  }, [cartLines]);

  const pricingTotals: SubscriptionCheckoutCartTotals | null = useMemo(() => {
    if (!totals) return null;
    return {
      totalEur: totals.totalEur,
      vatRate: totals.vatRate,
      addonLines: [],
    };
  }, [totals]);

  const lineAmountEur = (line: (typeof cartLines)[number]): number | undefined => {
    const key = `${line.subscriptionProductKey}|${line.scopeType ?? ''}|${line.scopeId ?? ''}`;
    return totals?.lineDeltas.find((d) => d.key === key)?.amountEur;
  };

  const serializeLines = useCallback(
    (lines: typeof cartLines) =>
      lines.map((l) => ({
        subscriptionProductKey: l.subscriptionProductKey,
        tier: l.tier,
        billingPeriod: 'ONCE' as const,
        additionalQuotaUnits: l.additionalQuotaUnits,
        scopeType: l.scopeType,
        scopeId: l.scopeId,
        parentSubscriptionInstanceId: l.parentSubscriptionInstanceId,
      })),
    [],
  );

  const handleRemoveLine = async (lineIndex: number) => {
    const remaining = cartLines.filter((_, i) => i !== lineIndex);
    try {
      if (!remaining.length) {
        await patchCart.mutateAsync({ clear: true });
      } else {
        await patchCart.mutateAsync({ lines: serializeLines(remaining) });
      }
    } catch {
      notification.notifyError(t('event.plan.cartSaveError'));
    }
  };

  const handleProceed = async () => {
    try {
      const { redirectUrl } = await checkoutMutation.mutateAsync(window.location.href);
      if (redirectUrl && redirectUrl !== window.location.href) {
        window.location.href = redirectUrl;
        return;
      }
      notification.notifySuccess(t('event.plan.checkoutApplied'));
      onClose();
    } catch {
      notification.notifyError(t('event.plan.checkoutError'));
    }
  };

  const billingAddress = useMemo(
    () => billingOrganization?.addresses.find((address) => address.type === ADDRESS_TYPE.BILLING),
    [billingOrganization?.addresses],
  );

  const canEditBillingAddress =
    !!billingOrganization?.id && !!permissions?.organizations?.[billingOrganization.id]?.includes('update');

  const editActiveOrganizationSchema = editActiveOrganizationSchemaFactory(t);
  const form = useForm<EditActiveOrganization>({
    resolver: zodResolver(editActiveOrganizationSchema),
    values: createFormDefaults(billingOrganization, billingAddress),
  });

  useEffect(() => {
    (async () => {
      const country = billingAddress?.country;
      if (!country) return;
      const countryCode = COUNTRY_TO_CODE_MAP[country as keyof typeof COUNTRY_TO_CODE_MAP];
      if (!countryCode) return;
      try {
        const result = await taxClassificationMutation({ countryCode });
        setTaxClassification(result.taxClassification);
      } catch {
        setTaxClassification(undefined);
      }
    })();
  }, [billingAddress?.country, taxClassificationMutation]);

  const latestOrderBillingCountry =
    billingAddress?.country && billingAddress.country !== '' ? billingAddress.country : undefined;

  const vatCountryDisplay = useMemo(() => {
    const vat = billingAddress?.vat;
    if (!latestOrderBillingCountry) return t('common.N/A');
    if (taxClassification === 'intraCommunity' && vat && vat !== '') {
      return t('event.checkoutModal.reverseCharge');
    }
    if (taxClassification === 'thirdPartyCountry') {
      return t('event.checkoutModal.thirdPartyCountry');
    }
    return capitalize(latestOrderBillingCountry);
  }, [billingAddress?.vat, latestOrderBillingCountry, taxClassification, t]);

  const billingAddressReset = useCallback(() => {
    form.reset(createFormDefaults(billingOrganization, billingAddress));
  }, [form, billingAddress, billingOrganization]);

  useEffect(() => {
    billingAddressReset();
    setIsEditingBillingAddress(false);
  }, [billingAddress, billingAddressReset]);

  useEffect(() => {
    if (!open) setEditingLine(null);
  }, [open]);

  const billingAddressOnSubmit = async (data: EditActiveOrganization) => {
    if (!billingOrganization) return;

    const nextBillingAddress = createBillingAddress(billingAddress, data.billingAddress);
    const updateData: PatchOrganizationDto = {
      name: data.general.name,
      addresses: [],
    };

    if (
      nextBillingAddress?.name &&
      nextBillingAddress?.address1 &&
      nextBillingAddress?.city &&
      nextBillingAddress?.country &&
      nextBillingAddress?.postCode
    ) {
      updateData.addresses.push({
        name: nextBillingAddress.name,
        address1: nextBillingAddress.address1,
        address2: nextBillingAddress.address2,
        city: nextBillingAddress.city,
        postCode: nextBillingAddress.postCode,
        country: nextBillingAddress.country,
        vat: nextBillingAddress.vat,
        type: ADDRESS_TYPE.BILLING,
      });
    }

    try {
      await editOrganizationMutation({
        id: billingOrganization.id,
        data: updateData,
      });
      // Keep active-org store in sync only when it is the same org being edited.
      if (activeOrganization?.id === billingOrganization.id) {
        setActiveOrganization({
          ...activeOrganization,
          addresses: updateData.addresses,
        });
      }
      setIsEditingBillingAddress(false);
    } catch {
      notification.notifyError(t('event.checkoutModal.notification.updateOrganizationBillingAddressError'));
    }
  };

  const hasAmount = (totals?.totalEur ?? 0) > 0;
  const hasLines = cartLines.length > 0;

  return (
    <>
      <Modal
        open={open}
        size={isWideScreen ? 'lg' : 'sm'}
        width={isWideScreen ? 1000 : undefined}
        minHeight={isWideScreen ? 360 : undefined}
        maxHeight={isWideScreen ? 'min(92vh, calc(100vh - 48px))' : undefined}
        sx={isWideScreen ? { overflow: 'hidden' } : undefined}
        fullScreen={!isWideScreen}
        data-testid="event-subscription-checkout-modal"
        handleClose={onClose}
        title={t('common.checkout')}
        description={t('event.plan.checkoutModalDescription')}
        contentSx={
          isWideScreen
            ? { minHeight: 0 }
            : {
                overflow: 'auto',
                minHeight: 0,
              }
        }
        footer={
          <>
            <Button
              onClick={onClose}
              variant="outlined"
              aria-label={t('subscription.organization.planPage.subscriptionCheckoutBackToPlansAria')}
            >
              {t('subscription.organization.planPage.subscriptionCheckoutBackToPlans')}
            </Button>
            <Button
              onClick={() => void handleProceed()}
              variant="contained"
              loading={checkoutMutation.isPending}
              disabled={
                !hasLines ||
                cartLoading ||
                (hasAmount && !billingAddress) ||
                !hasMonetizeAccess ||
                monetizeAccessLoading
              }
            >
              {hasAmount ? t('common.proceed') : t('event.plan.applyChanges')}
            </Button>
          </>
        }
      >
        {cartLoading && !checkoutCart ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              ...(isWideScreen ? { flex: 1, minHeight: 0 } : {}),
            }}
          >
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              sx={{
                flex: { xs: 'none', sm: 1 },
                minHeight: { xs: 'auto', sm: 0 },
                maxHeight: { xs: 'none', sm: '100%' },
                overflow: { xs: 'visible', sm: 'hidden' },
              }}
            >
              <Box
                sx={{
                  flex: { xs: 'none', sm: '1 1 0%' },
                  minWidth: 0,
                  minHeight: { xs: 'auto', sm: 0 },
                  overflowY: { xs: 'visible', sm: 'auto' },
                  pr: { sm: 0.5 },
                }}
              >
                {orderedLineIndices.map((lineIndex, orderPos) => {
                  const line = cartLines[lineIndex];
                  if (!line) return null;
                  const plan = catalogByKeyTier.get(`${line.subscriptionProductKey}|${line.tier}`);
                  const displayName = productDisplayName(t, line.subscriptionProductKey);
                  const claimText = resolveProductClaimText(t, line.subscriptionProductKey, line.tier, {
                    allowPlanWideClaim: false,
                  });
                  const showTierInTitle = (tierCountByProduct.get(line.subscriptionProductKey) ?? 0) > 1;
                  const tierTitle = showTierInTitle ? tierLabel(t, line.tier) : '';
                  const isAddon = line.subscriptionProductKey === EVENT_SUBSCRIPTION_PRODUCT_KEYS.STAGE_READY;
                  const prevIdx = orderPos > 0 ? orderedLineIndices[orderPos - 1] : undefined;
                  const prevIsAddon =
                    prevIdx !== undefined &&
                    cartLines[prevIdx]?.subscriptionProductKey === EVENT_SUBSCRIPTION_PRODUCT_KEYS.STAGE_READY;
                  const showAddonsHeading = isAddon && !prevIsAddon;
                  const dueToday = lineAmountEur(line);
                  const planKeys = plan ? collectQuotaKeysFromPlan(plan) : [];

                  return (
                    <Box key={`${line.subscriptionProductKey}-${line.scopeType}-${line.scopeId}-${lineIndex}`}>
                      {showAddonsHeading ? (
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ textTransform: 'uppercase', letterSpacing: 0.5, mb: 1, ml: 4 }}
                        >
                          {t('subscription.organization.planPage.subscriptionCheckoutAddons')}
                        </Typography>
                      ) : null}
                      <Box
                        sx={{
                          border: 1,
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 2,
                          mb: 2,
                          ...(isAddon ? { ml: 3 } : {}),
                        }}
                      >
                        <Stack
                          direction="row"
                          alignItems="flex-start"
                          justifyContent="space-between"
                          spacing={1}
                          sx={{ mb: claimText ? 1.5 : 1 }}
                        >
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography variant="h6" component="h3" sx={{ minWidth: 0, m: 0 }}>
                              {displayName}
                              {showTierInTitle ? (
                                <Typography
                                  component="span"
                                  variant="h6"
                                  color="text.secondary"
                                  sx={{ fontWeight: 500 }}
                                >
                                  {` | ${tierTitle}`}
                                </Typography>
                              ) : null}
                            </Typography>
                            {claimText ? (
                              <Typography variant="body2" color="text.secondary" sx={{ m: 0, mt: 0 }}>
                                {claimText}
                              </Typography>
                            ) : null}
                          </Box>
                          <Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
                            <CheckoutActionTooltip
                              wideScreen={isWideScreen}
                              title={t('subscription.organization.planPage.subscriptionCheckoutEditPlanTooltip')}
                            >
                              <IconButton
                                size="small"
                                color="primary"
                                aria-label={t('subscription.organization.planPage.subscriptionCheckoutEditPlanAria')}
                                disabled={patchPending}
                                onClick={() =>
                                  setEditingLine({
                                    subscriptionProductKey: line.subscriptionProductKey,
                                    scopeType: line.scopeType ?? 'Event',
                                    scopeId: line.scopeId ?? eventId,
                                  })
                                }
                              >
                                <EditIcon sx={{ fontSize: 20 }} />
                              </IconButton>
                            </CheckoutActionTooltip>
                            <CheckoutActionTooltip
                              wideScreen={isWideScreen}
                              title={t('subscription.organization.planPage.subscriptionCheckoutRemovePlanTooltip')}
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  color="inherit"
                                  aria-label={t(
                                    'subscription.organization.planPage.subscriptionCheckoutRemovePlanAria',
                                  )}
                                  disabled={patchPending}
                                  onClick={() => void handleRemoveLine(lineIndex)}
                                  sx={{ color: 'action.active' }}
                                >
                                  <DeleteOutlineIcon sx={{ fontSize: 20 }} />
                                </IconButton>
                              </span>
                            </CheckoutActionTooltip>
                          </Stack>
                        </Stack>

                        {plan
                          ? planKeys
                              .map((quotaKey) => {
                                const total =
                                  includedQuantityForKey(plan, quotaKey) +
                                  extraUnitsFor(line.additionalQuotaUnits, quotaKey);
                                if (total <= 0) return null;
                                return (
                                  <Typography
                                    key={`q-${lineIndex}-${quotaKey}`}
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ mb: 0.5 }}
                                  >
                                    {formatQuotaStepperCountLine(t, quotaKey, total)}
                                  </Typography>
                                );
                              })
                              .filter(Boolean)
                          : null}

                        <Box display="flex" justifyContent="flex-end" alignItems="flex-start" gap={2} pt={0.5}>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {t('subscription.organization.planPage.subscriptionCheckoutProrataLabel')}
                            </Typography>
                            <Typography variant="body1" fontWeight={600}>
                              {typeof dueToday === 'number' ? `€${dueToday.toFixed(2)}` : '—'}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t('event.checkoutModal.excludingVat')}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  );
                })}

                {!cartLines.length ? (
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('subscription.organization.planPage.subscriptionCheckoutEmpty')}
                  </Typography>
                ) : null}
              </Box>

              <Box
                sx={{
                  flex: { xs: 'none', sm: '1 1 0%' },
                  maxWidth: { xs: '100%', sm: 350 },
                  minWidth: 0,
                  boxSizing: 'border-box',
                  minHeight: { xs: 'auto', sm: 0 },
                  overflowY: { xs: 'visible', sm: 'auto' },
                  borderLeft: { xs: 0, sm: 1 },
                  borderTop: { xs: 1, sm: 0 },
                  borderColor: 'divider',
                  pl: { xs: 0, sm: 2 },
                  pt: { xs: 2, sm: 0 },
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ mb: pricingTotals ? 3 : 0, width: '100%', flexShrink: 0 }}>
                  {isEditingBillingAddress ? (
                    <Box
                      component="form"
                      onSubmit={form.handleSubmit(billingAddressOnSubmit)}
                      display="flex"
                      flexDirection="column"
                      alignItems="end"
                    >
                      <BillingForm control={form.control} setValue={form.setValue} clearErrors={form.clearErrors} />
                      <Box display="flex" justifyContent="end" gap={1} mt={2}>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => {
                            setIsEditingBillingAddress(false);
                            billingAddressReset();
                          }}
                        >
                          {t('common.cancel')}
                        </Button>
                        <Button type="submit" variant="contained" size="small" loading={editOrgPending}>
                          {t('common.save')}
                        </Button>
                      </Box>
                    </Box>
                  ) : (
                    <BillingAddressDisplay
                      billingAddress={billingAddress}
                      canEdit={canEditBillingAddress}
                      onEditClick={() => setIsEditingBillingAddress(true)}
                    />
                  )}
                </Box>

                {pricingTotals ? (
                  <Box
                    sx={{
                      width: '100%',
                      mt: { xs: 0, sm: 'auto' },
                      pb: 2,
                      flexShrink: 0,
                    }}
                  >
                    <OrganizationSubscriptionPricingDisplay
                      totals={pricingTotals}
                      latestOrderBillingCountry={latestOrderBillingCountry}
                      vatCountryDisplay={vatCountryDisplay}
                      loading={patchPending || cartLoading}
                      showBookedPlan={false}
                      showPlanLineBreakdown={false}
                    />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      component="p"
                      sx={{ mt: 2, lineHeight: 1.55, display: 'block' }}
                    >
                      {t('event.plan.checkoutLegalOnce')}
                    </Typography>
                    <CheckoutLegalProceedNotice />
                  </Box>
                ) : null}
              </Box>
            </Stack>
          </Box>
        )}
      </Modal>

      {editingLine ? (
        <EventManagePlanDialog
          open
          onClose={() => setEditingLine(null)}
          eventId={eventId}
          subscriptionProductKey={editingLine.subscriptionProductKey}
          scopeType={editingLine.scopeType}
          scopeId={editingLine.scopeId}
          onCartSaved={() => setEditingLine(null)}
          hideGoToCart
        />
      ) : null}
    </>
  );
}
